import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { faqData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, MessageCircle, ArrowRight, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = faqData[language] || faqData.fr;
  const [openQuestion, setOpenQuestion] = useState(null);
  const [activeCategory, setActiveCategory] = useState(content.categories[0]?.id);

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <HelpCircle className={`w-16 h-16 mx-auto mb-6 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="body-large">{content.subtitle}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className={`py-6 sticky top-20 z-30 border-b ${isDark ? 'bg-[#0C0A09]/95 backdrop-blur-xl border-white/10' : 'bg-white/95 backdrop-blur-xl border-stone-200'}`}>
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {content.categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#5B2D8E] text-white'
                    : isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {content.categories.map((category) => (
              <div
                key={category.id}
                className={activeCategory === category.id ? 'block' : 'hidden'}
              >
                <AnimatedSection>
                  <h2 className="heading-2 mb-8 text-center">{category.title}</h2>
                </AnimatedSection>
                
                <div className="space-y-4">
                  {category.questions.map((item, index) => {
                    const questionId = `${category.id}-${index}`;
                    const isOpen = openQuestion === questionId;
                    
                    return (
                      <AnimatedSection key={questionId} delay={index * 0.1}>
                        <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                          <button
                            onClick={() => toggleQuestion(questionId)}
                            className="w-full p-5 flex items-start justify-between text-left"
                          >
                            <span className="font-semibold text-theme-primary pr-4">{item.q}</span>
                            {isOpen ? (
                              <ChevronUp className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
                            ) : (
                              <ChevronDown className="w-5 h-5 flex-shrink-0 text-theme-muted" />
                            )}
                          </button>
                          
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className={`px-5 pb-5 pt-0 border-t ${isDark ? 'border-white/10' : 'border-stone-100'}`}>
                                  <p className="text-theme-secondary pt-4 leading-relaxed">
                                    {item.a}
                                  </p>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </AnimatedSection>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className={`py-12 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h3 className="heading-3 text-center mb-8">Pour aller plus loin</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link 
                  to="/scorecard"
                  className={`p-5 rounded-xl flex items-center gap-4 transition-colors ${isDark ? 'bg-[#292524] hover:bg-[#3D3836]' : 'bg-white hover:bg-stone-50'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <span className="font-semibold text-theme-primary block">{content.cta.scorecard}</span>
                    <span className="text-sm text-theme-secondary">Nos positions détaillées</span>
                  </div>
                  <ArrowRight className={`w-5 h-5 ml-auto ${isDark ? 'text-stone-500' : 'text-stone-400'}`} />
                </Link>
                
                <Link 
                  to="/notes"
                  className={`p-5 rounded-xl flex items-center gap-4 transition-colors ${isDark ? 'bg-[#292524] hover:bg-[#3D3836]' : 'bg-white hover:bg-stone-50'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <span className="text-2xl">📝</span>
                  </div>
                  <div>
                    <span className="font-semibold text-theme-primary block">{content.cta.notes}</span>
                    <span className="text-sm text-theme-secondary">Fiches de proposition en 1 page</span>
                  </div>
                  <ArrowRight className={`w-5 h-5 ml-auto ${isDark ? 'text-stone-500' : 'text-stone-400'}`} />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className={`py-16 ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <MessageCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h2 className="heading-3 mb-4">{content.cta.contact}</h2>
              <p className="text-theme-secondary mb-6">
                Vous ne trouvez pas la réponse à votre question ? Contactez-nous directement.
              </p>
              <Link to="/rejoindre" className="btn-primary">
                Nous contacter
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
